import React, { useCallback, useEffect, useState } from "react";
import DataSetupMainCard from "./DataSetupMainCard";
import DataSetupDrawer from "./DataSetupDrawer";
import { MuiTable, TextValidator, Autocomplete } from "components";
import {
  IconButton,
  Tooltip,
  Box,
  Chip,
  Dialog,
  Typography,
  CircularProgress,
  Switch,
  Grow,
  useTheme,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import StatusConfirmDialog from "components/StatusConfirmDialog";
import ModifierOptionService from "services/ModifierOptionService";
import ModifierService from "services/ModifierService";
import MenuItemService from "services/MenuItemService";

const ModifierOptionsSetupCard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [modifiers, setModifiers] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: null,
  });

  const [drawer, setDrawer] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    modifierId: null,
    menuItemId: null,
    price: "",
    displayOrder: "",
  });

  // Status update state
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    loading: false,
    optionId: null,
    optionName: "",
    newStatus: null,
  });

  // View modal state
  const [viewModal, setViewModal] = useState({
    open: false,
    item: null,
  });

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      limit: pageSize,
    }));
  };

  const getData = useCallback(async () => {
    setLoading(true);
    const response = await ModifierOptionService.getAll({
      ...filters,
      page: filters.page - 1,
    });
    if (response.success) {
      setData(response?.data?.view?.data || []);
      setTotalItems(response?.data?.view?.totalItems || 0);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    getData();
    fetchModifiers();
    fetchMenuItems();
  }, [getData]);

  const fetchModifiers = async () => {
    try {
      const response = await ModifierService.getAll({
        page: 0,
        limit: 100,
        isActive: true,
      });
      if (response.success) {
        setModifiers(response?.data?.view?.data || []);
      }
    } catch (error) {
      console.error("Error fetching modifiers:", error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await MenuItemService.getAll({
        page: 0,
        limit: 100,
        isActive: true,
      });
      if (response.success) {
        setMenuItems(response?.data?.view?.data || []);
      }
    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
  };

  const handleAddClick = () => {
    setIsEdit(false);
    setEditingId(null);
    setFormData({
      name: "",
      modifierId: null,
      menuItemId: null,
      price: "",
      displayOrder: "",
    });
    setDrawer(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleViewClick = (item) => {
    setViewModal({
      open: true,
      item: item,
    });
  };

  const handleEditClick = (item) => {
    setIsEdit(true);
    setEditingId(item.id);
    setFormData({
      name: item.name || "",
      modifierId:
        modifiers.find((m) => m.id === item.modifierId) ||
        item.modifierId ||
        null,
      menuItemId:
        menuItems.find((m) => m.id === item.menuItemId) ||
        item.menuItemId ||
        null,
      price: item.price || "",
      displayOrder: item.displayOrder || "",
    });
    setDrawer(true);
  };

  const handleStatusToggle = (item) => {
    setStatusDialog({
      open: true,
      loading: false,
      optionId: item.id,
      optionName: item.name,
      newStatus: !item.isActive,
    });
  };

  const handleStatusConfirm = async () => {
    const { optionId, newStatus } = statusDialog;

    try {
      setStatusDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        isActive: newStatus,
        companyId: filters.companyId,
      };

      const response = await ModifierOptionService.updateStatus(
        optionId,
        payload,
      );

      if (response.success) {
        await getData();
        console.log("✅ Modifier option status updated successfully");
      } else {
        console.error(
          "❌ Failed to update modifier option status:",
          response.error,
        );
      }

      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    } catch (error) {
      console.error("Error updating status:", error);
      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        modifierId: formData.modifierId?.id || formData.modifierId,
        menuItemId: formData.menuItemId?.id || formData.menuItemId,
        price: parseFloat(formData.price) || 0,
        displayOrder: parseInt(formData.displayOrder) || 0,
        companyId: filters.companyId,
      };

      if (isEdit) {
        await ModifierOptionService.update(editingId, payload);
      } else {
        await ModifierOptionService.create(payload);
      }

      setFormData({
        name: "",
        modifierId: null,
        menuItemId: null,
        price: "",
        displayOrder: "",
      });
      setEditingId(null);
      setDrawer(false);
      getData();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <DataSetupMainCard
      iconSrc="/assets/images/icons/options.png"
      title="Modifier Options"
      subtitle="Manage your modifier options"
      actionIconSrc="/assets/images/icons/plus.png"
      actionText="Add Option"
      onAddClick={handleAddClick}
    >
      <MuiTable
        columns={[
          {
            accessorKey: "actions",
            header: "Actions",
            Cell: ({ cell }) => {
              const item = cell.row.original;
              const isUpdating =
                statusDialog.optionId === item.id && statusDialog.loading;

              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleViewClick(item)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(item)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={item.isActive ? "Deactivate" : "Activate"}>
                    <Box sx={{ position: "relative" }}>
                      <Switch
                        checked={item.isActive}
                        size="small"
                        disabled={isUpdating}
                        onChange={() => handleStatusToggle(item)}
                      />
                      {isUpdating && (
                        <CircularProgress
                          size={20}
                          sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            opacity: 0.7,
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            },
          },
          {
            accessorKey: "name",
            header: "Name",
          },
          {
            accessorKey: "Modifier.name",
            header: "Modifier",
            Cell: ({ cell }) => cell.row.original.Modifier?.name || "-",
          },
          {
            accessorKey: "MenuItem.name",
            header: "Menu Item",
            Cell: ({ cell }) => cell.row.original.MenuItem?.name || "-",
          },
          {
            accessorKey: "price",
            header: "Price",
            Cell: ({ cell }) =>
              `LKR ${(Number(cell.getValue()) || 0).toFixed(2)}`,
          },
          {
            accessorKey: "displayOrder",
            header: "Display Order",
          },
          {
            accessorKey: "isActive",
            header: "Status",
            Cell: ({ cell }) => (
              <Chip
                label={cell.getValue() ? "Active" : "Inactive"}
                size="small"
                color={cell.getValue() ? "success" : "error"}
              />
            ),
          },
        ]}
        data={data}
        title=""
        manualPagination={true}
        rowCount={totalItems}
        currentPage={filters.page}
        pageSize={filters.limit}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
      />

      <DataSetupDrawer
        open={drawer}
        onClose={() => setDrawer(false)}
        iconSrc="/assets/images/icons/options.png"
        title={isEdit ? "Edit Modifier Option" : "Add Modifier Option"}
        subtitle="Create and manage modifier option details"
        submitLabel={isEdit ? "Update" : "Create"}
        onSubmit={handleFormSubmit}
      >
        <Box gap={2} display="flex" flexDirection="column">
          {/* Option Name */}
          <TextValidator
            label="Option Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            validators={["required", "minStringLength:2"]}
            errorMessages={[
              "Option name is required",
              "Option name must be at least 2 characters",
            ]}
            fullWidth
            size="small"
            placeholder="Enter option name (e.g., Extra Spicy)"
          />

          {/* Modifier (Autocomplete) */}
          <Autocomplete
            name="modifierId"
            label="Modifier"
            value={formData.modifierId}
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                modifierId: newValue,
              }));
            }}
            options={modifiers}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            validators={["required"]}
            errorMessages={["Modifier is required"]}
            fullWidth
            size="small"
          />

          {/* Menu Item (Autocomplete) */}
          <Autocomplete
            name="menuItemId"
            label="Menu Item"
            value={formData.menuItemId}
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                menuItemId: newValue,
              }));
            }}
            options={menuItems}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            validators={["required"]}
            errorMessages={["Menu Item is required"]}
            fullWidth
            size="small"
          />

          {/* Price */}
          <TextValidator
            label="Price"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            validators={["isNumber"]}
            errorMessages={["Price must be a number"]}
            fullWidth
            size="small"
            type="number"
            placeholder="Enter price"
            inputProps={{ step: "0.01", min: "0" }}
          />

          {/* Display Order */}
          <TextValidator
            label="Display Order"
            name="displayOrder"
            value={formData.displayOrder}
            onChange={handleInputChange}
            validators={["required", "isNumber"]}
            errorMessages={[
              "Display order is required",
              "Display order must be a number",
            ]}
            fullWidth
            size="small"
            type="number"
            placeholder="Enter display order"
            inputProps={{ min: "0" }}
          />
        </Box>
        <></>
      </DataSetupDrawer>

      {/* Status Confirmation Dialog */}
      <StatusConfirmDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={handleStatusConfirm}
        title={
          statusDialog.newStatus
            ? "Activate Modifier Option"
            : "Deactivate Modifier Option"
        }
        message={
          statusDialog.newStatus
            ? "Are you sure you want to activate this modifier option? It will be visible to customers."
            : "Are you sure you want to deactivate this modifier option? It will no longer be visible to customers."
        }
        itemName={statusDialog.optionName}
        newStatus={statusDialog.newStatus}
        loading={statusDialog.loading}
      />

      {/* View Modifier Option Modal */}
      <Dialog
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={{
          enter: 400,
          exit: 300,
        }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            maxHeight: "90vh",
            boxShadow: `0 20px 60px rgba(0, 0, 0, 0.15)`,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            transition: "all 0.4s ease-in-out",
          },
        }}
      >
        {/* ===== Header with Icon ===== */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Icon Container */}
          <Box
            component="img"
            src="/assets/images/icons/options.png"
            alt="Option Icon"
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              objectFit: "contain",
              filter:
                "brightness(0) saturate(100%) invert(43%) sepia(99%) saturate(1235%) hue-rotate(244deg)",
              opacity: 0.8,
            }}
          />

          {/* Title and Subtitle */}
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
                letterSpacing: 0.2,
              }}
            >
              Modifier Option Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              View option information
            </Typography>
          </Box>

          {/* Close Button */}
          <IconButton
            onClick={() => setViewModal({ open: false, item: null })}
            sx={{
              mt: -0.5,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ===== Content ===== */}
        <Box
          sx={{
            p: 3,
            flexGrow: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.background.default,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: 3,
              "&:hover": {
                backgroundColor: theme.palette.text.disabled,
              },
            },
          }}
        >
          {viewModal.item && (
            <Stack spacing={3}>
              {/* Option Name */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.name}
                </Typography>
              </Box>

              {/* Modifier */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Modifier
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.Modifier?.name || "-"}
                </Typography>
              </Box>

              {/* Menu Item */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Menu Item
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.MenuItem?.name || "-"}
                </Typography>
              </Box>

              {/* Price */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Price
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  LKR {(Number(viewModal.item.price) || 0).toFixed(2)}
                </Typography>
              </Box>

              {/* Display Order */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Display Order
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.displayOrder}
                </Typography>
              </Box>

              {/* Status */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Status
                </Typography>
                <Chip
                  label={viewModal.item.isActive ? "Active" : "Inactive"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: viewModal.item.isActive
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.light}15 100%)`,
                    color: viewModal.item.isActive
                      ? theme.palette.success.dark
                      : theme.palette.warning.dark,
                    border: `2px solid ${
                      viewModal.item.isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: viewModal.item.isActive
                        ? `0 4px 12px ${theme.palette.success.main}40`
                        : `0 4px 12px ${theme.palette.warning.main}40`,
                    },
                  }}
                />
              </Box>
            </Stack>
          )}
        </Box>
      </Dialog>
    </DataSetupMainCard>
  );
};

export default ModifierOptionsSetupCard;
